// @flow
/** @jsx jsx */

import { Children, type Element, type Node, cloneElement } from 'react';

import { AsyncIndicator } from '@zapier/common-components';
import { jsx } from '@emotion/core';

type Props = {
  children: Element<any>,
  /** Fixes scroll position and takes up entire screen */
  isFixed: boolean,
  /** Is indicator visible */
  isVisible: boolean,
  /** Used to render a custom Indicator */
  renderAsyncIndicator: () => Node,
};

const overlayCss = ({ isFixed }: Props) => ({
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  left: 0,
  position: isFixed ? 'fixed' : 'absolute',
  top: 0,
  width: '100%',
  zIndex: 901, // Used to render on top of dropdown menus
});

const renderDefaultIndicator = () => <AsyncIndicator />;

/**
 * Renders a loading indicator on top of children content
 */
const AsyncOverlay = (props: Props) => {
  const { props: childrenProps } = props.children;
  const { style: childrenStyle = {} } = childrenProps;

  // Clone the child and set it's position to relative if needed.
  // This allows the overlay to seamlessly cover the whole
  // area of the child.
  return cloneElement(
    props.children,
    {
      ...childrenProps,
      style: {
        ...childrenStyle,
        position: childrenStyle.position || 'relative',
      },
    },
    [
      ...Children.toArray(props.children.props.children),
      props.isVisible && (
        <div css={overlayCss(props)} key="indicator">
          {props.renderAsyncIndicator()}
        </div>
      ),
    ]
  );
};

AsyncOverlay.defaultProps = {
  isFixed: false,
  isVisible: false,
  renderAsyncIndicator: renderDefaultIndicator,
};

/** @component */
export default AsyncOverlay;
